/**
 * Sparkline (Phase 5 Action 48).
 *
 * Tiny 7-point SVG line chart for Fleet Overview cells. No axes, no
 * labels — a glanceable "shape of activity" indicator. Hover shows
 * the raw values as a title tooltip.
 *
 * Design choices:
 *   • Fixed width/height defaults keep table cells uniform.
 *   • All-zero series render as a flat baseline (not empty) so the
 *     column still shows something for the many "quiet week" rows we
 *     expect early on.
 *   • Renders the last point as a small dot so the eye lands on "now"
 *     rather than the leftmost history.
 */

import clsx from 'clsx';

interface Props {
  values: number[];
  width?: number;
  height?: number;
  strokeClass?: string; // tailwind color for the stroke
  fillClass?: string;   // tailwind color for the last-point dot
}

export default function Sparkline({
  values,
  width = 72,
  height = 24,
  strokeClass = 'stroke-slate-400',
  fillClass = 'fill-slate-500',
}: Props) {
  if (!values.length) return <span className="text-slate-400">—</span>;

  const max = Math.max(...values, 1); // divide-by-zero guard
  const stepX = values.length > 1 ? width / (values.length - 1) : 0;
  const points = values
    .map((v, i) => {
      const x = i * stepX;
      // Invert Y so higher values are visually up. Leave a 2px pad top
      // and bottom so the dot at the last point isn't clipped.
      const y = height - 2 - ((v / max) * (height - 4));
      return `${x.toFixed(1)},${y.toFixed(1)}`;
    })
    .join(' ');
  const lastPoint = values.length > 0 ? points.split(' ').pop() : null;
  const lastCoords = lastPoint ? lastPoint.split(',').map(Number) : [0, 0];
  const title = `Last ${values.length} days: [${values.join(', ')}]`;

  return (
    <svg
      width={width}
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      className="inline-block align-middle"
      role="img"
      aria-label={title}
    >
      <title>{title}</title>
      <polyline
        points={points}
        fill="none"
        strokeWidth={1.5}
        className={clsx(strokeClass)}
      />
      <circle
        cx={lastCoords[0]}
        cy={lastCoords[1]}
        r={2}
        className={clsx(fillClass)}
      />
    </svg>
  );
}
